import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sun, Crown, Trash2, Plus, Save, Power, RotateCcw, Users, Camera, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";

export default function DayUseManager() {
  const [event, setEvent] = useState(null);
  const [confirmations, setConfirmations] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [form, setForm] = useState({ date: "", time: "", pix_key: "", pix_holder: "", info: "" });
  const [newRank, setNewRank] = useState({ name: "", instagram: "", points: 0 });
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [pickUser, setPickUser] = useState("");
  const photoRefs = useRef({});

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR")))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/dayuse");
      setEvent(res.data.event);
      setConfirmations(res.data.confirmations || []);
      setRanking(res.data.ranking || []);
      if (res.data.event) {
        const e = res.data.event;
        setForm({
          date: e.date || "",
          time: e.time || "",
          pix_key: e.pix_key || "",
          pix_holder: e.pix_holder || "",
          info: e.info || "",
        });
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveEvent = async () => {
    setSaving(true);
    try {
      await api.put("/admin/dayuse/event", form);
      toast.success("Informações salvas!");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const newList = async () => {
    if (!window.confirm("Criar uma NOVA lista? Os nomes atuais ficarão arquivados e a lista pública começará vazia.")) return;
    try {
      await api.post("/admin/dayuse/event/new", form);
      toast.success("Nova lista criada e ativada!");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggle = async () => {
    const next = !(event && event.active);
    try {
      await api.post("/admin/dayuse/toggle", { active: next });
      toast.success(next ? "Lista ativada — visível para os clientes." : "Lista desativada — oculta para os clientes (dados salvos).");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const delConf = async (id) => {
    try {
      await api.delete(`/admin/dayuse/confirmations/${id}`);
      setConfirmations((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addRank = async () => {
    if (!newRank.name.trim()) return toast.error("Informe o nome do vencedor.");
    try {
      const res = await api.post("/admin/dayuse/ranking", {
        name: newRank.name.trim(),
        instagram: newRank.instagram.trim(),
        points: Number(newRank.points) || 0,
      });
      setRanking((prev) =>
        [...prev, res.data].sort((a, b) => b.points - a.points)
      );
      setNewRank({ name: "", instagram: "", points: 0 });
      toast.success("Vencedor adicionado ao ranking!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const addFromUser = async () => {
    if (!pickUser) return toast.error("Selecione um login.");
    try {
      await api.post("/admin/dayuse/ranking", { user_id: pickUser, points: 0 });
      setPickUser("");
      await load();
      toast.success("Jogador adicionado ao ranking!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const uploadPhoto = async (id, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/admin/dayuse/ranking/${id}/photo`, fd); await load(); toast.success("Foto atualizada!"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removePhoto = async (id) => {
    try { await api.post(`/admin/dayuse/ranking/${id}/photo/remove`); await load(); toast.success("Foto personalizada removida."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const updateRank = async (r) => {
    try {
      await api.patch(`/admin/dayuse/ranking/${r.id}`, {
        name: r.name.trim(),
        instagram: (r.instagram || "").trim(),
        points: Number(r.points) || 0,
      });
      setRanking((prev) => [...prev].sort((a, b) => b.points - a.points));
      toast.success("Ranking atualizado.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const delRank = async (id) => {
    try {
      await api.delete(`/admin/dayuse/ranking/${id}`);
      setRanking((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setRankField = (id, field, value) =>
    setRanking((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const active = event && event.active;

  return (
    <div className="mt-8" data-testid="dayuse-manager">
      <div className="flex items-center gap-2">
        <Sun className="h-5 w-5 text-[#1B7A45]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">
          DayUse / Rotativo
        </h2>
        <span
          data-testid="dayuse-status-badge"
          className={`ml-2 rounded-full px-2.5 py-0.5 text-xs font-bold ${
            active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"
          }`}
        >
          {active ? "ATIVA" : "INATIVA"}
        </span>
      </div>
      <p className="mt-1 text-sm text-slate-500">
        Configure data, horário e PIX. Desative para ocultar dos clientes (dados ficam salvos).
      </p>

      {/* Event editor */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs font-semibold text-slate-500">Data</label>
            <Input
              data-testid="dayuse-admin-date"
              placeholder="Ex.: Domingo, 28/09"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-1 rounded-xl border-slate-200"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Horário</label>
            <Input
              data-testid="dayuse-admin-time"
              placeholder="Ex.: 08h às 12h"
              value={form.time}
              onChange={(e) => setForm({ ...form, time: e.target.value })}
              className="mt-1 rounded-xl border-slate-200"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Chave PIX</label>
            <Input
              data-testid="dayuse-admin-pixkey"
              placeholder="Ex.: 48 9 9951 0417"
              value={form.pix_key}
              onChange={(e) => setForm({ ...form, pix_key: e.target.value })}
              className="mt-1 rounded-xl border-slate-200"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Titular do PIX</label>
            <Input
              data-testid="dayuse-admin-pixholder"
              placeholder="Ex.: Felipe Dubal Martins"
              value={form.pix_holder}
              onChange={(e) => setForm({ ...form, pix_holder: e.target.value })}
              className="mt-1 rounded-xl border-slate-200"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-slate-500">Observação (opcional)</label>
            <Input
              data-testid="dayuse-admin-info"
              placeholder="Ex.: Valor R$ 20 por pessoa"
              value={form.info}
              onChange={(e) => setForm({ ...form, info: e.target.value })}
              className="mt-1 rounded-xl border-slate-200"
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            data-testid="dayuse-admin-save"
            onClick={saveEvent}
            disabled={saving}
            className="rounded-full bg-[#1B7A45] text-white hover:bg-[#156337]"
          >
            <Save className="mr-1.5 h-4 w-4" /> Salvar
          </Button>
          <Button
            data-testid="dayuse-admin-toggle"
            onClick={toggle}
            variant="outline"
            className={`rounded-full ${
              active
                ? "border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                : "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            }`}
          >
            <Power className="mr-1.5 h-4 w-4" /> {active ? "Desativar lista" : "Ativar lista"}
          </Button>
          <Button
            data-testid="dayuse-admin-new"
            onClick={newList}
            variant="outline"
            className="rounded-full border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100"
          >
            <RotateCcw className="mr-1.5 h-4 w-4" /> Criar nova lista
          </Button>
        </div>
      </div>

      {/* Confirmations */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[#1B7A45]" />
          <h3 className="font-display text-base font-bold text-slate-900">
            Confirmados ({confirmations.length})
          </h3>
        </div>
        <div className="mt-3 space-y-2">
          {confirmations.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-400">Nenhum nome confirmado ainda.</p>
          ) : (
            confirmations.map((c, i) => (
              <div
                key={c.id}
                data-testid={`dayuse-admin-conf-${c.id}`}
                className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2"
              >
                <span className="text-sm text-slate-700">
                  <span className="font-bold">{i + 1}.</span> {c.name}
                  {c.instagram && <span className="ml-2 text-xs text-[#E8611A]">{c.instagram}</span>}
                </span>
                <Button
                  data-testid={`dayuse-admin-del-conf-${c.id}`}
                  onClick={() => delConf(c.id)}
                  size="sm"
                  variant="outline"
                  className="h-7 rounded-full border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Ranking manager */}
      <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <Crown className="h-4 w-4 text-[#E8611A]" />
          <h3 className="font-display text-base font-bold text-slate-900">Ranking · REI DO DAYUSE</h3>
        </div>

        {/* add from logins */}
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <select data-testid="ranking-pick-user" value={pickUser} onChange={(e) => setPickUser(e.target.value)} className="w-full min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
            <option value="">Adicionar login do app ao ranking...</option>
            {users.filter((u) => !ranking.some((r) => r.user_id === u.user_id)).map((u) => <option key={u.user_id} value={u.user_id}>{u.name} {u.email && !u.email.endsWith("@dubal.local") ? `— ${u.email}` : ""}</option>)}
          </select>
          <Button data-testid="ranking-add-user-btn" onClick={addFromUser} className="rounded-xl bg-[#1B7A45] text-white hover:bg-[#156337]"><UserPlus className="mr-1.5 h-4 w-4" /> Adicionar</Button>
        </div>
        <p className="mt-3 text-xs text-slate-400">Ou adicione um nome manualmente:</p>

        {/* add new */}
        <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_90px_auto]">
          <Input
            data-testid="ranking-add-name"
            placeholder="Nome do vencedor"
            value={newRank.name}
            onChange={(e) => setNewRank({ ...newRank, name: e.target.value })}
            className="rounded-xl border-slate-200"
          />
          <Input
            data-testid="ranking-add-instagram"
            placeholder="@instagram"
            value={newRank.instagram}
            onChange={(e) => setNewRank({ ...newRank, instagram: e.target.value })}
            className="rounded-xl border-slate-200"
          />
          <Input
            data-testid="ranking-add-points"
            type="number"
            placeholder="Pts"
            value={newRank.points}
            onChange={(e) => setNewRank({ ...newRank, points: e.target.value })}
            className="rounded-xl border-slate-200"
          />
          <Button
            data-testid="ranking-add-btn"
            onClick={addRank}
            className="rounded-xl bg-[#E8611A] text-white hover:bg-[#CF5212]"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>

        {/* rows */}
        <div className="mt-3 space-y-2">
          {ranking.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-400">Nenhum vencedor cadastrado.</p>
          ) : (
            ranking.map((r, i) => (
              <div
                key={r.id}
                data-testid={`ranking-row-${r.id}`}
                className="grid grid-cols-1 items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 p-2 sm:grid-cols-[auto_1fr_1fr_90px_auto_auto]"
              >
                <div className="flex items-center gap-2">
                  <span className="w-6 text-center text-xs font-black text-slate-400">{i + 1}º</span>
                  <button data-testid={`ranking-photo-${r.id}`} onClick={() => photoRefs.current[r.id]?.click()} title="Trocar foto" className="group relative h-10 w-10 overflow-hidden rounded-full bg-slate-200">
                    {mediaUrl(r.photo) ? <img src={mediaUrl(r.photo)} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-sm font-bold text-slate-500">{(r.name || "?")[0]}</span>}
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100"><Camera className="h-4 w-4 text-white" /></span>
                  </button>
                  <input ref={(el) => (photoRefs.current[r.id] = el)} type="file" accept="image/*" className="hidden" data-testid={`ranking-photo-input-${r.id}`} onChange={(e) => uploadPhoto(r.id, e)} />
                  {r.has_photo && <button data-testid={`ranking-photo-remove-${r.id}`} onClick={() => removePhoto(r.id)} className="text-[10px] font-semibold text-slate-400 hover:text-red-500">usar foto do login</button>}
                </div>
                <Input
                  value={r.name}
                  onChange={(e) => setRankField(r.id, "name", e.target.value)}
                  className="rounded-lg border-slate-200 bg-white"
                />
                <Input
                  value={r.instagram || ""}
                  placeholder="@instagram"
                  onChange={(e) => setRankField(r.id, "instagram", e.target.value)}
                  className="rounded-lg border-slate-200 bg-white"
                />
                <Input
                  type="number"
                  value={r.points}
                  onChange={(e) => setRankField(r.id, "points", e.target.value)}
                  className="rounded-lg border-slate-200 bg-white"
                />
                <Button
                  data-testid={`ranking-save-${r.id}`}
                  onClick={() => updateRank(r)}
                  size="sm"
                  className="rounded-lg bg-[#1B7A45] text-white hover:bg-[#156337]"
                >
                  <Save className="h-4 w-4" />
                </Button>
                <Button
                  data-testid={`ranking-del-${r.id}`}
                  onClick={() => delRank(r.id)}
                  size="sm"
                  variant="outline"
                  className="rounded-lg border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
